import { useApolloClient } from '@apollo/client'
import { MarketplaceDbIntegration } from 'generated/resources/datatable.graphql'
import type { IntegrationsSyncableTypeEnum } from 'generated/resources/graphql'
import {
  IntegrationsSyncableStateFieldsFragmentDoc,
  SyncableStateUpdatedDocument,
} from 'generated/resources/graphql'

import { useSubscription } from 'modules/core/hooks/useSubscription'

interface UseSyncableStateUpdatedSubscriptionProps {
  marketplaceIntegration: MarketplaceDbIntegration | null
  syncEnabled: boolean
}

// TODO: IntegrationsFramework: This should be part of the configuration and be distributed
const syncableTypeTypenameMap: Record<IntegrationsSyncableTypeEnum, string> = {
  compensationscompensation: 'CompensationsPayrollRunEmployeesCompensation',
  expensesexpensable: 'ExpensesExpensable',
}

export const useSyncableStateUpdatedSubscription = ({
  marketplaceIntegration,
  syncEnabled,
}: UseSyncableStateUpdatedSubscriptionProps) => {
  const client = useApolloClient()

  // This is a very sensitive part of the framework functionality that ensures realtime sync. Please be careful when modifying it and testing it.
  // Note that SyncableStateUpdated Subscription is used to subscribe both Update and Create actions on SyncableState
  // That's why we need to modify the syncable (parent) cache.
  // Updating only the SyncabelState cache won't cover Create action.
  useSubscription(SyncableStateUpdatedDocument, {
    onData: ({ data: subscriptionData }) => {
      const syncableState = subscriptionData.data?.integrationsSyncableStateUpdate?.syncableState

      if (!syncableState || !marketplaceIntegration) return
      const syncableTypename = syncableTypeTypenameMap[syncableState.syncableType]
      if (!syncableTypename) {
        throw new Error(`Missing syncable type mapping for ${syncableState.syncableType}.`)
      }

      const syncableCacheId = client.cache.identify({
        __typename: syncableTypename,
        id: syncableState.syncableId,
      })

      if (!syncableCacheId) return
      client.cache.modify({
        id: syncableCacheId,
        fields: {
          syncableState(existingConnection) {
            const newSyncableStateRef = client.writeFragment({
              id: `IntegrationsSyncableState:${syncableState.id}`,
              fragment: IntegrationsSyncableStateFieldsFragmentDoc,
              fragmentName: 'IntegrationsSyncableStateFields',
              data: {
                id: syncableState.id,
                errorMessages: syncableState.errorMessages,
                marketplaceIntegrationId: marketplaceIntegration.id,
                status: syncableState.status,
                statusUpdatedAt: syncableState.statusUpdatedAt,
                syncableId: syncableState.syncableId,
                syncableType: syncableState.syncableType,
                __typename: 'IntegrationsSyncableState',
              },
            })

            return [...existingConnection, newSyncableStateRef]
          },
        },
      })
    },
    skip: !syncEnabled,
  })
}
